import React, { useState, useEffect, useCallback } from 'react';
import { 
  Film, 
  Play, 
  Trash2, 
  Edit, 
  Send, 
  Clock,
  Sparkles,
  Loader2,
  Filter,
  MoreVertical,
  Copy,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as api from '@/lib/api';

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    rendering: 'bg-secondary/20 text-secondary border-secondary/30',
    ready: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
    published: 'bg-green-500/20 text-green-500 border-green-500/30',
    failed: 'bg-destructive/20 text-destructive border-destructive/30'
  };

  return (
    <Badge variant="outline" className={styles[status] || ''}>
      {status === 'rendering' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
      {status}
    </Badge>
  );
};

const PlatformBadge = ({ platform }) => {
  const styles = {
    youtube: 'bg-red-500',
    tiktok: 'bg-gradient-to-r from-cyan-400 to-pink-500',
    instagram: 'bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500'
  };

  return (
    <span className={`inline-block w-5 h-5 rounded text-[10px] font-bold text-white flex items-center justify-center ${styles[platform]}`}>
      {platform[0].toUpperCase()}
    </span>
  );
};

const ClipCard = ({ clip, onDelete, onGenerateTitles, onSchedule }) => {
  const [showTitles, setShowTitles] = useState(false);
  const [titles, setTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(false);

  const handleGenerateTitles = async () => {
    setLoadingTitles(true);
    try {
      const response = await api.generateViralTitles(clip.id);
      setTitles(response.data.titles);
      setShowTitles(true);
    } catch (error) {
      toast.error('Failed to generate titles');
    } finally {
      setLoadingTitles(false);
    }
  };

  return (
    <>
      <Card className="clip-card bg-card border-border overflow-hidden group">
        <div className="aspect-video relative overflow-hidden">
          <img 
            src={clip.thumbnail_url || 'https://images.unsplash.com/photo-1660832459346-d069f58410c1?w=320&h=180&fit=crop'} 
            alt={clip.title}
            className="clip-thumbnail w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button size="icon" variant="secondary" className="rounded-full">
              <Play className="w-5 h-5" />
            </Button>
          </div>
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
            <Badge className="bg-black/60 text-white border-none">
              <Clock className="w-3 h-3 mr-1" />
              {clip.duration_seconds}s
            </Badge>
            <div className="flex gap-1">
              {clip.platforms?.map((p) => (
                <PlatformBadge key={p} platform={p} />
              ))}
            </div>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-medium text-sm line-clamp-2 flex-1" title={clip.title}>
              {clip.title}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleGenerateTitles}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Viral Titles
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onSchedule(clip)}>
                  <Send className="w-4 h-4 mr-2" />
                  Schedule Publish
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigator.clipboard.writeText(clip.id)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy ID
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(clip.id)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
            {clip.original_channel}
          </p>
          
          <div className="flex items-center justify-between">
            <StatusBadge status={clip.status} />
            <span className="text-xs text-muted-foreground">
              CTR: {((clip.predicted_ctr || 0) * 100).toFixed(1)}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Titles Dialog */}
      <Dialog open={showTitles} onOpenChange={setShowTitles}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI-Generated Viral Titles
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {titles.map((item, index) => (
              <div 
                key={`title-${item.title?.slice(0, 20) || index}-${index}`}
                className="p-3 rounded-lg bg-muted/50 border border-border hover:border-primary/50 cursor-pointer transition-colors"
                onClick={() => {
                  navigator.clipboard.writeText(item.title);
                  toast.success('Title copied to clipboard');
                }}
              >
                <p className="font-medium text-sm">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Predicted CTR: {(item.predicted_ctr * 100).toFixed(1)}%
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function Clips() {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [scheduleClip, setScheduleClip] = useState(null);
  const [schedulePlatform, setSchedulePlatform] = useState('youtube');
  const [scheduling, setScheduling] = useState(false);

  const fetchClips = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === 'all' ? null : filter;
      const response = await api.getClips(status);
      setClips(response.data.clips || []);
    } catch (error) {
      toast.error('Failed to load clips');
      setClips([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  const handleDelete = async (clipId) => {
    try {
      await api.deleteClip(clipId);
      setClips(clips.filter(c => c.id !== clipId));
      toast.success('Clip deleted');
    } catch (error) {
      toast.error('Failed to delete clip');
    }
  };

  const handleSchedule = async () => {
    if (!scheduleClip) return;
    setScheduling(true);
    try {
      await api.schedulePublish({
        clip_id: scheduleClip.id,
        platform: schedulePlatform
      });
      toast.success(`Scheduled for ${schedulePlatform}`);
      setScheduleClip(null);
    } catch (error) {
      toast.error('Failed to schedule publish');
    } finally {
      setScheduling(false);
    }
  };

  const filteredClips = clips.filter(clip => 
    clip.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clip.original_channel?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="clips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Clips Library</h1>
          <p className="text-muted-foreground">Manage your generated viral clips</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Input
              placeholder="Search clips..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 pl-9 bg-muted/50"
              data-testid="search-clips-input"
            />
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 bg-muted/50" data-testid="filter-select">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clips</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rendering">Rendering</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Clips', value: clips.length, color: 'primary' },
          { label: 'Ready', value: clips.filter(c => c.status === 'ready').length, color: 'blue-500' },
          { label: 'Published', value: clips.filter(c => c.status === 'published').length, color: 'green-500' },
          { label: 'Rendering', value: clips.filter(c => c.status === 'rendering').length, color: 'secondary' }
        ].map((stat) => (
          <Card key={stat.label} className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              <p className="text-2xl font-heading font-bold">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Clips Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array(8).fill(0).map((_, i) => (
            <Card key={`skeleton-clip-${i}`} className="bg-card border-border overflow-hidden">
              <Skeleton className="aspect-video w-full" />
              <CardContent className="p-4 space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-24" />
                <div className="flex justify-between">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredClips.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredClips.map((clip) => (
            <ClipCard 
              key={clip.id} 
              clip={clip}
              onDelete={handleDelete}
              onSchedule={setScheduleClip}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <Film className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No clips found</p>
            <p className="text-sm text-muted-foreground">
              {filter !== 'all' 
                ? `No clips with status "${filter}"`
                : 'Add podcasts from Discovery to generate clips'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Schedule Dialog */}
      <Dialog open={!!scheduleClip} onOpenChange={() => setScheduleClip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Schedule Publish
            </DialogTitle>
          </DialogHeader>
          {scheduleClip && (
            <div className="py-4 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Clip</p>
                <p className="font-medium">{scheduleClip.title}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Platform</label>
                <Select value={schedulePlatform} onValueChange={setSchedulePlatform}>
                  <SelectTrigger data-testid="platform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube Shorts</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="instagram">Instagram Reels</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleClip(null)}>
              Cancel
            </Button>
            <Button onClick={handleSchedule} disabled={scheduling}>
              {scheduling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
